<template>
  <div>
    <!--
      Compact calendar shell.
      The wrapper has an explicit viewport-based height so FullCalendar can
      use height: '100%' and fit without scrolling. dayMaxEvents collapses
      overflow into a "+N more" popover so cells stay short instead of
      growing the row.
    -->
    <div class="calendar calendar--compact">
      <FullCalendar  ref="calendar" v-bind:options="calendarOptions"/>
    </div>

    <!--
      Event-details modal (brand new UI; replaces the per-event b-popover loop).
      All logic preserved:
        - opened by onEventClick(eventInfo) which sets selectedEvent / selectedEventIndex
        - dismiss / edit / close use the same handlers as before
        - body still rendered by <calendar-event-basic> so the inner data flow
          (clientSelect, updated, accept/reject) keeps working as-is
    -->
    <b-modal v-model="eventModal.show"
             :title="null"
             size="lg"
             centered
             hide-header
             hide-footer
             modal-class="cal-event-modal"
             body-class="p-0"
             content-class="cal-event-modal__content"
             no-close-on-backdrop
    >
      <template v-if="selectedEvent">
        <!-- Hero header: gradient tinted by the event TYPE color -->
        <div class="cal-event-modal__hero" :style="{ '--apt-accent': typeColorOf(selectedEvent) }">
          <div class="cal-event-modal__hero-icon">
            <feather-icon :icon="typeIconOf(selectedEvent)" size="20"/>
          </div>
          <div class="cal-event-modal__title">
            <div class="cal-event-modal__date">
              <i class="fa-regular fa-calendar mr-25"></i>
              <span>{{ formatRange(selectedEvent.start, selectedEvent.end) }}</span>
            </div>
            <h3 class="mb-0">{{ selectedEvent.title || $t('calendar.details') }}</h3>
            <div class="cal-event-modal__pills mt-1">
              <span v-if="selectedEvent.type" class="cal-event-modal__pill">
                {{ $t(`calendarTypes.${selectedEvent.type}.name`) }}
              </span>
              <span v-if="selectedEvent.status === 'CANCELLED'"
                    class="cal-event-modal__pill cal-event-modal__pill--dark">
                <i class="fa-solid fa-ban mr-25"></i>{{ $t('calendar.cancelled') }}
              </span>
            </div>
          </div>
          <button type="button" class="cal-event-modal__close"
                  @click="closeEventModal" :title="$t('operations.close')">
            <feather-icon icon="XIcon" size="18"/>
          </button>
        </div>

        <!-- Body: existing detail component, just in a nicer frame -->
        <div class="cal-event-modal__body">
          <calendar-event-basic v-bind:event="selectedEvent"
                                @clientSelect="onClientSelect"
                                @updated="ev=>updateEvent(selectedEventIndex, ev)"/>
        </div>

        <!-- Footer: action bar — same handlers the popover used -->
        <div class="cal-event-modal__footer">
          <b-button v-if="selectedEvent.userId === myId"
                    variant="outline-danger" size="sm"
                    @click="dismissEvent(selectedEventIndex, selectedEvent.id)">
            <feather-icon icon="ArchiveIcon" class="mr-1"/>{{ $t('operations.dismiss') }}
          </b-button>
          <b-button variant="outline-primary" size="sm"
                    @click="editEvent(selectedEvent.id)">
            <feather-icon icon="Edit3Icon" class="mr-1"/>{{ $t('operations.edit') }}
          </b-button>
          <b-button variant="primary" size="sm" @click="closeEventModal">
            {{ $t('operations.close') }}
          </b-button>
        </div>
      </template>
    </b-modal>

    <calendar-event-modal ref="eventModal" @submitted="refreshCalendar"/>
    <apt-confirm-modal ref="confirmModal"/>
  </div>
</template>

<script>
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import allLocales from '@fullcalendar/core/locales-all'
import listPlugin from '@fullcalendar/list'
import { mapActions, mapGetters } from 'vuex'
import calendarEventBasic from '@/components/partial/calendar/calendarEventBasic'
import mixinBase from '@/components/mixin/mixinBase'
import mixinNotifications from '@/components/mixin/mixinNotifications'
import mixinTable from '@/components/mixin/mixinTable'
import CalendarEventModal from '@/components/modal/calendarEventModal'
import { CalendarStatusEnum } from '@/model/calendar/CalendarStatusEnum'
import mixinPopover from '@/components/mixin/mixinPopover'
import { calendarTypeColor, calendarTypeMeta } from '@/model/calendar/calendarMeta'
import AptConfirmModal from '@/components/modal/AptConfirmModal'
export default {
  name: 'Calendar',
  mixins: [mixinBase, mixinPopover, mixinNotifications, mixinTable],
  components: {
    CalendarEventModal,
    FullCalendar,
    calendarEventBasic,
    AptConfirmModal
  },
  data() {
    return {
      calendarOptions: {
        fixedWeekCount: false,
        // Fit to the wrapper (which is sized to the viewport in CSS below)
        // instead of growing tall enough to need page scroll.
        height: '100%',
        expandRows: true,
        // Collapse overflowing day cells into a "+N more" popover (styled below).
        dayMaxEvents: 4,
        locales: allLocales,
        locale: this.$t('lang.base') || 'en',//this.currentLang, // the initial locale
        plugins: [dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin],
        headerToolbar: {
          start: 'prev,next today',
          center: 'title',
          end: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek',
        },
        initialView: 'dayGridMonth',
        firstDay: 1,
        nowIndicator: true,
        navLinks: true,
        slotMinTime: '06:00:00',
        slotMaxTime: '22:00:00',
        scrollTime: '08:00:00',
        editable: true,
        // Render timed events as filled pills in EVERY view (month view
        // otherwise uses "dot" style: transparent background + theme text,
        // which made the white event text invisible in light mode).
        eventDisplay: 'block',
        // Click or drag an empty slot to create an event prefilled with the range.
        selectable: true,
        select: this.onRangeSelect,
        eventDrop: this.persistEventTiming,
        eventResize: this.persistEventTiming,
        events: this.onCalendarRefresh,
        eventClick: this.onEventClick,
        eventDidMount: this.onEventDidMount,
        slotLabelFormat: {
          hour: '2-digit',
          minute: '2-digit',
          omitZeroMinute: false,
          hour12: false
        },
        eventTimeFormat: {
          hour: '2-digit',
          minute: '2-digit',
          omitZeroMinute: false,
          hour12: false
        }

      },

      events: [],
      cal: { start: '', end: '' },
      loadedExternalEvents: {},
      dialogs: {
        popovers: false
      },
      // Event-details modal state. selectedEventIndex is the index into
      // this.events so updateEvent / dismissEvent keep working with the
      // same (index, ev) signatures they used under the popover flow.
      eventModal: { show: false },
      selectedEvent: null,
      selectedEventIndex: -1,
      listeners:{
        refresh(){}
      }
    }
  },
  props:{
    types:Array,
    users:Array,
  },
  watch: {
    types:{
      handler() {
        this.refreshCalendar()
      },
      // immediate: true
    },
    users:{
      handler() {
        this.refreshCalendar()
      },
      // immediate: true
    },
    // The page toolbar renders the spinner / refresh control now.
    loading(v) {
      this.$emit('loading', v)
    }
  },
  created() {
    console.log('calendar created')
    this.calendarOptions.locale = 'en'
    this.$root.$on('crm::calendar::refresh',this.refreshCalendar)
  },
  beforeDestroy() {
    this.$root.$off('crm::calendar::refresh',this.refreshCalendar)
  },
  computed: {
    ...mapGetters('user', ['myId']),
  },
  methods: {
    ...mapActions('calendar', ['getExternalCalendars', 'getCalendars', 'cancelEvent', 'updateCalendar']),
    refreshCalendar() {
      let calendarApi = this.$refs.calendar.getApi()
      calendarApi.refetchEvents()
    },
    onCalendarRefresh(info, successCallback, failureCallback) {
      this.cal.start = this.$moment(info.start).startOf('month')
          .format('YYYY-MM-DD')
      this.cal.end = this.$moment(info.end).endOf('month')
          .format('YYYY-MM-DD')
      this.refresh()
          .then(() => successCallback(this.adaptEvents()), failureCallback)
    },
    refresh() {
      let $this = this
      let token = this.setLoading();
      return new Promise((resolve, reject) => {
        this.getCalendars({
          from: $this.cal.start,
          until: $this.cal.end,
          // Whose calendars to load. Empty + CALENDAR_READ_ALL = the whole
          // team (backend expands to the user + all his subordinates).
          usersId: Array.isArray(this.users) ? this.users : []
        })
            .then(
                (events) => {
                  $this.updateEvents(events)
                  resolve()
                },
                reject
            )
      }).finally(() => {
        this.setLoaded(token)
      })
    },
    updateEvents(apiEvents) {
      if (!Array.isArray(apiEvents)) {
        apiEvents = []
      }
      this.events=apiEvents.map(e=>({...e, external : false}));
      // Let the page (sidebar) know: it derives the visible count and the
      // "pending invites" panel from the raw events.
      this.$emit('events-loaded', this.events)
      return this.events
    },
    adaptEvents() {
      let events =  this.events.filter(this.eventsFilter).map(ev => {
        const color = calendarTypeColor(ev.type)
        const myBadge = this.badgeVariant(ev)
        const muted = ev.status === CalendarStatusEnum.CANCELLED || myBadge === 'danger'
        const requested = myBadge === 'dark' && ev.status !== CalendarStatusEnum.CANCELLED
        return {
          start: this.$moment.serverDateTime(ev.start).toServerDateTimeFormat(),
          end: this.$moment.serverDateTime(ev.end).toServerDateTimeFormat(),
          title: (ev.status === CalendarStatusEnum.CANCELLED ? `[${this.$t('calendar.cancelled')}] ` : '') + this.eventTitleForPreview(ev),
          id: 'ev-' + ev.id,
          rawId: ev.id,
          backgroundColor: color,
          borderColor: color,
          classNames: [
            'apt-event-pill',
            muted ? 'apt-event--muted' : '',
            requested ? 'apt-event--requested' : '',
          ].filter(Boolean),
          editable: (ev.userId === this.myId) && ev.status !== CalendarStatusEnum.CANCELLED,
        }
      })
      return events;
    },
    // Click-drag (or click) on an empty slot → create modal prefilled with the range.
    onRangeSelect(sel) {
      let start = this.$moment(sel.start)
      let end = sel.end ? this.$moment(sel.end) : null
      if (sel.allDay) {
        // All-day cell click: default to a 09:00 start, let the modal apply
        // the type's default duration.
        start = start.hour(9).minute(0)
        end = null
      }
      this.$refs['eventModal'].showModal(null, start.format('YYYY-MM-DD HH:mm'),
          end ? end.format('YYYY-MM-DD HH:mm') : null)
    },
    badgeVariant(event){
      if(event == null || event.userId === this.myId){
        return 'primary';
      }
      let userData = event.users.find(u=>u.userId === this.myId)
      if(userData == null || userData.status == null || userData.status === CalendarStatusEnum.PENDING){
        return 'dark';
      }
      if(userData.status === CalendarStatusEnum.APPROVED){
        return 'success';
      }
      if(userData.status === CalendarStatusEnum.REJECTED){
        return 'danger';
      }
      return 'dark';
    },
    eventTitleForPreview(event){
      if (event.clientsIds==null || event.clientsIds.length === 0 || event.clientNames == null) {
        return event.title;
      }

      let clientId = event.clientsIds[0];
      return event.clientNames[clientId];

    },
    eventsFilter(ev) {
      if(!Array.isArray(this.types)){
        return true;
      }
      if(!this.types.includes('cancelled') && ev.status === CalendarStatusEnum.CANCELLED){
        return false;
      }
      if(!this.types.includes('rejected') && this.badgeVariant(ev) === 'danger'){
        return false;
      }
      if(!this.types.includes('approved') && this.badgeVariant(ev) === 'success'){
        return false;
      }
      if(!this.types.includes('pending') && this.badgeVariant(ev) === 'dark'){
        return false;
      }
      return this.types.includes(ev.type);
    },
    onEventDidMount: function (eventInfo) {
      let $this = this;
      eventInfo.el.id = eventInfo.event.id

      eventInfo.el.ondblclick = function (){
        $this.editEvent(eventInfo.event.extendedProps.rawId)
      }
      this.refreshPopovers()
    },
    refreshPopovers() {
      let $this = this
      //Hack in order to reload the popovers
      this.dialogs.popovers = false
      setTimeout(() => {
        this.dialogs.popovers = true
        $this.$forceUpdate()
      }, 1000)
    },

    onDateClick(arg) {
      this.$refs['eventModal'].showModal(null, arg.dateStr);
    },
    // Drag & drop / resize → persist the new timing; revert the pill on failure.
    persistEventTiming(info) {
      const $this = this
      const rawId = info.event.extendedProps && info.event.extendedProps.rawId
      const event = this.events.find(e => e.id === rawId)
      if (event == null) {
        info.revert()
        return
      }
      const start = this.$moment(info.event.start)
      // A drop with no end (e.g. into an all-day slot) defaults to +30 min.
      const end = info.event.end ? this.$moment(info.event.end) : start.clone().add(30, 'minutes')
      const updated = {
        ...event,
        users: (event.users || []).map(u => u.userId),
        start: start.format('YYYY-MM-DD HH:mm'),
        end: end.format('YYYY-MM-DD HH:mm'),
      }
      this.updateCalendar({ calendar: updated, id: event.id })
          .then(
              ev => {
                const idx = $this.events.findIndex(e => e.id === ev.id)
                if (idx >= 0) $this.events.splice(idx, 1, ev)
                $this.$emit('events-loaded', $this.events)
              },
              err => {
                info.revert()
                $this.showErrorNotification($this.$t('globalMessages.updateDataError', { system: $this.$t('menu.calendar') }), $this.getErrorDescription(err))
              }
          )
    },
    editEvent(id) {
      // Close the details modal first so the edit modal doesn't stack on
      // top of it (visually messy, and prevents focus-trap fights).
      this.closeEventModal()
      this.$refs['eventModal'].showModal(id);
      this.closePopover()
    },
    onEventClick(eventInfo) {
      // Resolve the clicked FC event back to the raw event in this.events
      // via rawId (set in adaptEvents). We keep the index around so the
      // existing updateEvent(index, ev) / dismissEvent(index, id) signatures
      // continue to work unchanged.
      const rawId = eventInfo.event.extendedProps && eventInfo.event.extendedProps.rawId
      const idx = this.events.findIndex(e => e.id === rawId)
      if (idx < 0) {
        // Fallback: fire the legacy popover signal in case anything still
        // listens. Should never hit in practice.
        this.$root.$emit('bv::show::popover', 'ev' + eventInfo.event.id)
        return
      }
      this.selectedEventIndex = idx
      this.selectedEvent = this.events[idx]
      this.eventModal.show = true
    },
    closeEventModal() {
      this.eventModal.show = false
      this.selectedEvent = null
      this.selectedEventIndex = -1
    },
    // Legacy helper — popover loop is gone but several call-sites (and
    // potentially external mixins) still invoke this. Kept as a no-op that
    // also closes the new modal so the perceived behavior is the same.
    closePopover(popover) {
      this.closeEventModal()
      if (popover != null && typeof popover === 'string') {
        this.$root.$emit('bv::hide::popover', popover)
      } else {
        this.$root.$emit('bv::hide::popover')
      }
    },
    typeColorOf(ev) {
      return calendarTypeColor(ev && ev.type)
    },
    typeIconOf(ev) {
      return calendarTypeMeta(ev && ev.type).icon
    },
    // Compact human-readable range for the modal hero. Same datetime
    // format the rest of the app uses (via the momentDateTime filter and
    // $moment helper installed app-wide).
    formatRange(start, end) {
      if (!start) return ''
      const s = this.$moment(start)
      const e = end ? this.$moment(end) : null
      if (!e || !e.isValid()) return s.format('ddd D MMM YYYY · HH:mm')
      if (s.isSame(e, 'day')) {
        return s.format('ddd D MMM YYYY · HH:mm') + ' – ' + e.format('HH:mm')
      }
      return s.format('D MMM · HH:mm') + ' → ' + e.format('D MMM · HH:mm')
    },
    onClientSelect(id) {
      console.log('calendar-> onClientSelect(' + id + ')')
      this.$root.$emit('crm::client::select', id)
    },
    async dismissEvent(index, id) {
      // Close the details modal so the confirm dialog isn't competing with
      // it for focus/backdrop. The result toast still fires on completion.
      this.closeEventModal()
      this.closeAllPopovers()
      const result = await this.$refs.confirmModal.open({
        title: this.$t('operations.dismiss'),
        message: this.$t('calendar.action.dismissMsg', { event: this.events[index].title }),
        okText: this.$t('operations.dismiss'),
      })
      if(!result){
        return
      }
      let $this = this
      this.cancelEvent(id)
          .then(
              ev => {
                $this.updateEvent(index, ev)
              },
              err => $this.showErrorNotification($this.$t('globalMessages.updateDataError', { system: $this.$t('menu.clients') }), err.error)
          )
    },
    updateEvent(index, ev) {
      let event = this.events[index];
      if(event.id === ev.id){
        this.events[index] = ev;
        this.refreshPopovers();
        this.refreshCalendar();
      }
    },
  }
}
</script>

<style lang="scss">

.dark-layout{
  // Loud today highlight in dark mode — bright indigo border, tinted
  // background, and a glowing day-number pill. Mirrors the light-mode
  // treatment with adjusted contrast for the dark canvas.
  .fc .fc-day-today {
    background-color: rgba(115, 103, 240, 0.22) !important;
    box-shadow: inset 0 0 0 2px #7367f0 !important;
  }
  .fc .fc-day-today .fc-daygrid-day-top::after {
    content: 'TODAY';
    position: absolute;
    top: 4px;
    right: 6px;
    font-size: 0.6rem;
    font-weight: 800;
    letter-spacing: 0.05em;
    color: #ffffff;
    background: #7367f0;
    padding: 1px 5px;
    border-radius: 4px;
    z-index: 1;
    pointer-events: none;
  }
  .fc .fc-day-today .fc-daygrid-day-number {
    background-color: #7367f0 !important;
    color: #ffffff !important;
    border-radius: 999px;
    font-weight: 700;
    box-shadow: 0 0 0 4px rgba(115, 103, 240, 0.35),
                0 2px 6px rgba(115, 103, 240, 0.55);
  }
  .fc .fc-col-header-cell.fc-day-today {
    background-color: #7367f0 !important;
  }
  .fc .fc-col-header-cell.fc-day-today .fc-col-header-cell-cushion {
    color: #ffffff !important;
    font-weight: 800;
  }

  .fc .fc-list .fc-list-day-cushion {
    background-color: rgba(27, 35, 55, 0.82) !important;
  }

  // Dark-mode polish for the redesigned toolbar / cells / events.
  // Mirrors the light-mode tints but with adjusted opacities so they read
  // on the navy background.
  .calendar.calendar--compact {
    .fc .fc-scrollgrid,
    .fc .fc-scrollgrid td,
    .fc .fc-scrollgrid th,
    .fc .fc-theme-standard td,
    .fc .fc-theme-standard th {
      border-color: rgba(255, 255, 255, 0.08);
    }
    .fc .fc-col-header-cell {
      background-color: rgba(115, 103, 240, 0.10);
    }
    .fc .fc-col-header-cell-cushion {
      color: #d0d2d6;
    }
    .fc .fc-daygrid-day-number {
      color: #d0d2d6;
    }
    .fc .fc-day-sat,
    .fc .fc-day-sun {
      background-color: rgba(255, 255, 255, 0.02);
    }
    .fc .fc-day-other {
      background-color: rgba(255, 255, 255, 0.03);
    }
    .fc .fc-button-group {
      background: rgba(255, 255, 255, 0.05);
    }
    .fc .fc-button-group > .fc-button {
      color: #b4b7bd;
    }
    .fc .fc-button-group > .fc-button.fc-button-active {
      background: #283046;
      color: #ffffff;
    }
    .fc .fc-prev-button,
    .fc .fc-next-button {
      color: #b4b7bd;
    }
    .fc-daygrid-event {
      box-shadow: 0 1px 2px rgba(0,0,0,0.30);
    }
    .fc-daygrid-event:hover {
      box-shadow: 0 3px 8px rgba(0,0,0,0.45);
    }
  }
}

// Viewport-fitted compact calendar. Height is computed to leave room for
// the app navbar, the card padding around it, and a small breathing
// margin — so the calendar fills the screen without ever scrolling the
// page. dayMaxEvents (set in JS) keeps cell heights from blowing up.
.calendar.calendar--compact {
  height: calc(100vh - 180px);
  min-height: 480px;
  position: relative;

  // ----- Overall canvas -----
  // Rounded card-like container, subtle outer border, soft hover shadow.
  // Picks up the surrounding b-card's background so dark mode just works.
  .fc {
    border-radius: 12px;
    overflow: hidden;
    background: transparent;
    font-family: inherit;
  }
  // The outermost FullCalendar table border is harsh by default — soften
  // it to a hairline and tint with the theme grey.
  .fc .fc-scrollgrid,
  .fc .fc-scrollgrid td,
  .fc .fc-scrollgrid th,
  .fc .fc-theme-standard td,
  .fc .fc-theme-standard th {
    border-color: rgba(34, 41, 47, 0.08);
  }

  // ----- Header toolbar -----
  // Polished header: bigger title, segmented pill button group for the
  // view switcher, ghost-style prev/next.
  .fc .fc-toolbar.fc-header-toolbar {
    margin-bottom: 0.75rem;
    padding: 0.25rem 0;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
  .fc .fc-toolbar-title {
    font-size: 1.3rem;
    font-weight: 700;
    letter-spacing: -0.01em;
    margin: 0 0.5rem;
    text-transform: capitalize;
  }
  .fc .fc-button {
    padding: 0.32rem 0.7rem;
    font-size: 0.78rem;
    font-weight: 600;
    border-radius: 6px;
    border: 1px solid transparent;
    background-color: rgba(115, 103, 240, 0.10);
    color: #7367f0;
    box-shadow: none !important;
    text-transform: capitalize;
    transition: background-color .15s ease, color .15s ease, transform .05s ease;
  }
  .fc .fc-button:hover {
    background-color: rgba(115, 103, 240, 0.20);
    color: #5e50ee;
  }
  .fc .fc-button:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.25) !important;
  }
  .fc .fc-button:active { transform: translateY(1px); }
  .fc .fc-button-primary:not(:disabled).fc-button-active,
  .fc .fc-button-primary:not(:disabled):active {
    background-color: #7367f0;
    color: #ffffff;
    border-color: #7367f0;
  }
  // Segment the view-switcher group on the right into a single pill.
  .fc .fc-button-group {
    background: rgba(115, 103, 240, 0.08);
    border-radius: 8px;
    padding: 2px;
    gap: 2px;
  }
  .fc .fc-button-group > .fc-button {
    border-radius: 6px;
    background: transparent;
    color: #6e6b7b;
  }
  .fc .fc-button-group > .fc-button.fc-button-active {
    background: #ffffff;
    color: #7367f0;
    box-shadow: 0 1px 3px rgba(0,0,0,0.08) !important;
  }
  // Prev/next: small icon-only ghost buttons.
  .fc .fc-prev-button,
  .fc .fc-next-button {
    background: transparent;
    color: #6e6b7b;
    width: 32px;
    padding: 0;
  }
  .fc .fc-prev-button:hover,
  .fc .fc-next-button:hover {
    background: rgba(115, 103, 240, 0.12);
    color: #7367f0;
  }

  // ----- Weekday header row -----
  // Uppercase, tracked, muted — looks like a modern admin dashboard.
  .fc .fc-col-header-cell {
    padding: 8px 4px;
    background-color: rgba(115, 103, 240, 0.04);
  }
  .fc .fc-col-header-cell-cushion {
    color: #6e6b7b;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    text-decoration: none !important;
    padding: 4px 6px;
  }

  // ----- Day cells -----
  // Soft hover, gentle weekend tint, generous but compact padding.
  .fc .fc-daygrid-day {
    transition: background-color .15s ease;
  }
  .fc .fc-daygrid-day:hover {
    background-color: rgba(115, 103, 240, 0.05);
  }
  .fc .fc-day-sat,
  .fc .fc-day-sun {
    background-color: rgba(0, 0, 0, 0.015);
  }
  .fc .fc-day-other {
    background-color: rgba(0, 0, 0, 0.02);
  }
  .fc .fc-day-other .fc-daygrid-day-number {
    opacity: 0.4;
  }
  .fc .fc-daygrid-day-top {
    padding: 4px 6px 0 6px;
  }
  .fc .fc-daygrid-day-number {
    font-size: 0.8rem;
    font-weight: 600;
    color: #5e5873;
    padding: 2px 6px;
    line-height: 1.1;
    text-decoration: none !important;
  }
  // Day cells collapse overflow into a "+N more" link (dayMaxEvents: 4).
  .fc .fc-daygrid-day-frame {
    position: relative;
    height: 100%;
  }
  .fc .fc-daygrid-more-link {
    font-size: 0.72rem;
    font-weight: 700;
    color: #7367f0;
    padding: 1px 6px;
    border-radius: 6px;
    &:hover { background: rgba(115, 103, 240, 0.12); text-decoration: none; }
  }
  // "+N more" popover
  .fc .fc-popover {
    border: 1px solid rgba(34, 41, 47, 0.10);
    border-radius: 10px;
    box-shadow: 0 8px 28px rgba(0, 0, 0, 0.16);
    overflow: hidden;
  }
  .fc .fc-popover-header {
    padding: 6px 10px;
    font-weight: 700;
    font-size: 0.78rem;
  }

  // ----- Events -----
  // Pill-style events: rounded corners, subtle elevation on hover. Bumped
  // padding + font + line-height so titles are actually legible (previous
  // version was too cramped). min-height locks a comfortable row height
  // even when the title is one short word.
  .fc-daygrid-event {
    padding: 4px 8px;
    font-size: 0.8rem;
    line-height: 1.35;
    min-height: 22px;
    margin: 3px 5px 0 5px;
    border-radius: 6px;
    border: none !important;
    box-shadow: 0 1px 2px rgba(0,0,0,0.04);
    transition: transform .1s ease, box-shadow .15s ease;
    cursor: pointer;
    display: flex;
    align-items: center;
  }
  .fc-daygrid-event:hover {
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(0,0,0,0.10);
  }
  .fc-daygrid-event .fc-event-time {
    font-weight: 700;
    opacity: 0.85;
    margin-right: 5px;
    font-size: 0.75rem;
  }
  .fc-daygrid-event .fc-event-title {
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  // ----- Time-grid (week / day) views -----
  // Quieter grid lines, accented "now" indicator.
  .fc .fc-timegrid-slot {
    height: 2.2em;
  }
  .fc .fc-timegrid-slot-label-cushion,
  .fc .fc-timegrid-axis-cushion {
    font-size: 0.72rem;
    color: #6e6b7b;
    font-weight: 500;
  }
  .fc .fc-timegrid-now-indicator-line {
    border-color: #ea5455;
    border-width: 2px;
  }
  .fc .fc-timegrid-now-indicator-arrow {
    border-color: #ea5455;
  }

  // ----- List view -----
  .fc .fc-list {
    border-radius: 10px;
    border-color: rgba(34, 41, 47, 0.08);
  }
  .fc .fc-list-day-cushion {
    background-color: rgba(115, 103, 240, 0.06) !important;
    font-weight: 700;
  }
  .fc .fc-list-event:hover td {
    background-color: rgba(115, 103, 240, 0.08);
  }

  // Today highlight (light mode) — VERY loud. Tinted cell background, a
  // thick 2px indigo border on all four sides of the cell, plus a large
  // filled indigo circle around the day number with a glow. Impossible
  // to miss even with the cell packed with events.
  .fc .fc-day-today {
    background-color: rgba(115, 103, 240, 0.18) !important;
    box-shadow: inset 0 0 0 2px #7367f0 !important;
    position: relative;
  }
  // Small "TODAY" badge in the top-RIGHT corner of the cell so the day
  // number stays on the left, aligned with every other day in the grid.
  .fc .fc-day-today .fc-daygrid-day-top::after {
    content: 'TODAY';
    position: absolute;
    top: 4px;
    right: 6px;
    font-size: 0.6rem;
    font-weight: 800;
    letter-spacing: 0.05em;
    color: #7367f0;
    background: #ffffff;
    padding: 1px 5px;
    border-radius: 4px;
    box-shadow: 0 0 0 1px #7367f0;
    z-index: 1;
    pointer-events: none;
  }
  .fc .fc-day-today .fc-daygrid-day-number {
    background-color: #7367f0;
    color: #ffffff !important;
    border-radius: 999px;
    font-weight: 700;
    font-size: 0.85rem;
    min-width: 26px;
    height: 26px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 0 0 4px rgba(115, 103, 240, 0.30),
                0 2px 6px rgba(115, 103, 240, 0.45);
    margin: 2px;
  }
  // Make the "today" column header also stand out in the week/day views.
  .fc .fc-col-header-cell.fc-day-today {
    background-color: #7367f0 !important;
  }
  .fc .fc-col-header-cell.fc-day-today .fc-col-header-cell-cushion {
    color: #ffffff !important;
    font-weight: 800;
  }
}

.calendar{
  // Event pills — colored by their TYPE (backgroundColor set per event).
  .apt-event-pill {
    color: #fff !important;
    .fc-event-main, .fc-event-time, .fc-event-title { color: #fff !important; }
  }
  // List view keeps its own colors readable.
  .fc-list .apt-event-pill {
    color: inherit !important;
    .fc-event-title { color: inherit !important; }
  }
  // Cancelled / rejected-by-me → faded + struck through.
  .apt-event--muted {
    opacity: 0.45;
    .fc-event-title { text-decoration: line-through !important; }
  }
  // Waiting for MY answer (participant status PENDING) → dashed outline + "?".
  .apt-event--requested {
    border: 2px dashed rgba(255, 255, 255, 0.9) !important;
    opacity: 0.85;
    .fc-event-title::after {
      content: ' ?';
      font-weight: 800;
    }
  }
}

// =====================================================================
// Event-details modal (brand new UI; replaces the old b-popover loop).
// Lives outside .calendar because b-modal is teleported to <body>.
// =====================================================================
.cal-event-modal {
  .cal-event-modal__content {
    border-radius: 14px;
    overflow: hidden;
    border: none;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18);
  }

  // Hero strip — gradient tinted live by the event TYPE color (--apt-accent),
  // matching the create/edit modal's signature header.
  .cal-event-modal__hero {
    position: relative;
    display: flex;
    align-items: center;
    gap: .9rem;
    padding: 1.1rem 1.4rem;
    color: #ffffff;
    background: linear-gradient(120deg, var(--apt-accent, #7367f0), #4b4b9b);
  }
  .cal-event-modal__hero-icon {
    width: 44px;
    height: 44px;
    border-radius: .65rem;
    background: rgba(255, 255, 255, .18);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .cal-event-modal__title {
    flex: 1;
    min-width: 0;
  }

  .cal-event-modal__title h3 {
    color: #ffffff;
    font-weight: 700;
    font-size: 1.4rem;
    margin: 0.25rem 0 0 0;
    word-break: break-word;
  }
  .cal-event-modal__date {
    font-size: 0.8rem;
    font-weight: 600;
    letter-spacing: 0.03em;
    opacity: 0.92;
    text-transform: uppercase;
  }

  .cal-event-modal__pills {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .cal-event-modal__pill {
    display: inline-flex;
    align-items: center;
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    padding: 3px 10px;
    border-radius: 999px;
    background: rgba(255,255,255,0.22);
    color: #ffffff;
    backdrop-filter: blur(2px);
  }
  .cal-event-modal__pill--dark { background: rgba(0,0,0,0.25); }

  // Round close button in the hero corner (rotates on hover like the form modal).
  .cal-event-modal__close {
    color: #ffffff;
    background: rgba(255,255,255,0.15);
    width: 32px;
    height: 32px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    flex-shrink: 0;
    align-self: flex-start;
    transition: background .15s ease, transform .2s ease;
  }
  .cal-event-modal__close:hover {
    background: rgba(255,255,255,0.30);
    transform: rotate(90deg);
  }

  // Body wraps the existing calendarEventBasic component. Padding here
  // (not on the body via b-modal) so the hero can run edge-to-edge.
  .cal-event-modal__body {
    padding: 1.25rem 1.5rem 0.5rem 1.5rem;
    max-height: 60vh;
    overflow-y: auto;
  }

  // Footer action bar — divider above, right-aligned buttons.
  .cal-event-modal__footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 0.5rem;
    padding: 1rem 1.5rem;
    border-top: 1px solid rgba(34, 41, 47, 0.08);
    background: rgba(0, 0, 0, 0.015);
  }
}

// Dark-mode delta for the modal.
.dark-layout .cal-event-modal {
  .cal-event-modal__content {
    background: #283046;
    color: #d0d2d6;
  }
  .cal-event-modal__footer {
    background: rgba(255, 255, 255, 0.02);
    border-top-color: rgba(255, 255, 255, 0.08);
  }
}
</style>
